/** @jsx jsx */
import React, { useCallback, type PointerEvent, type FocusEvent } from 'react';

import { css, jsx } from '@compiled/react';
import { getATLContextUrl } from '@atlaskit/atlassian-context';
import Heading from '@atlaskit/heading';
import { Box, Text } from '@atlaskit/primitives';
import { N0, N20, N90, B200 } from '@atlaskit/theme/colors';
import { token } from '@atlaskit/tokens';
import { FormattedMessage, useIntl } from '@atlassian/jira-intl';
import { Link, useRouterActions } from '@atlassian/react-resource-router';
import { HorizontalStackBarChart } from '@atlassian/viz-platform-charts--next/charts';
import { element, useElementProps } from '@atlassian/viz-platform-charts--next/core';
import { useSectionAnalytics } from '../../../common/ui/analytics/index.tsx';
import { ChartWithHeaderContainer } from '../../../common/ui/chart-with-header-container/index.tsx';
import { EmptyState } from '../../../common/ui/empty-state/index.tsx';
import { useSummaryFeatures } from '../../../controllers/features-context/index.tsx';
import { escapeFilterChars } from '../../utils.tsx';
import { BoundaryIds, SectionIds } from '../../../common/types.tsx';
import messages from './messages.tsx';

type EpicProgressProps = {
	data: {
		hierarchyName?: string;
		levelOneIssueTypeNames: string[];
		progress: {
			name: string;
			key: string;
			iconUrl: string;
			count: number;
			status: string;
		}[][];
	};
};

type BarDatum = {
	count: number;
};

type Bar = {
	key: string;
	x: number;
	y: number;
	width: number;
	height: number;
	datum: BarDatum;
};

type BarStack = {
	key: string;
	bars: Bar[];
};

export const CustomLayout = element(
	({
		epicStatus,
		legendOnly,
	}: {
		epicStatus: EpicProgressProps['data']['progress'][0];
		legendOnly: boolean;
	}) => {
		const { children } = useElementProps(HorizontalStackBarChart.elements.Layout);

		if (legendOnly) return <div css={legendContainerStyles}>{children}</div>;

		return (
			<div css={layoutStyles}>
				<Box>
					<Link href={`/browse/${epicStatus[0].key}`} css={epicInfoContainerStyles}>
						<img css={iconStyles} alt="" src={epicStatus[0].iconUrl} />
						<Text color="color.text.subtle">{epicStatus[0].key}</Text>
						<Text>{epicStatus[0].name}</Text>
					</Link>
				</Box>
				<div css={percentageBarContainerStyles}>{children}</div>
			</div>
		);
	},
	{ as: HorizontalStackBarChart.elements.Layout },
);

const legendContainerStyles = css({
	display: 'flex',
	flexGrow: 1,
	height: '32px',
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
	lineHeight: '16px',
	// need to only show the legend which is from external library
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'> div': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors
		'&:nth-of-type(2)': {
			display: 'none',
		},
	},
});

const layoutStyles = css({
	display: 'flex',
	flexDirection: 'column',
	'&:hover': {
		backgroundColor: token('elevation.surface.hovered', N20),
	},
	paddingTop: token('space.075', '6px'),
	paddingRight: 0,
	paddingBottom: token('space.075', '6px'),
	paddingLeft: 0,
	borderRadius: token('border.radius', '3px'),
	marginBottom: token('space.050', '4px'),
});

const epicInfoContainerStyles = css({
	display: 'inline-flex',
	alignItems: 'center',
	gap: token('space.100', '8px'),
	marginBottom: token('space.050', '4px'),
	marginLeft: token('space.050', '4px'),

	'&:hover': {
		textDecoration: 'none',
	},
});

const iconStyles = css({
	width: '16px',
	height: '16px',
});

const percentageBarContainerStyles = css({
	display: 'flex',
	minWidth: '0',
	minHeight: '0',
	flexGrow: 1,
	height: '24px',
});

const HorizontalStackBarChartScales = HorizontalStackBarChart.elements.Scales;

export const CustomScalesElement = element(
	() => {
		const elementProps = useElementProps(HorizontalStackBarChartScales);

		const renderChildren = useCallback<typeof elementProps.children>(
			(scales) => {
				// scales is not typed correctly in the library
				// eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/consistent-type-assertions
				const typedScales = scales as any;
				typedScales.yScale.padding(0);
				return elementProps.children(typedScales);
			},
			[elementProps],
		);

		return <HorizontalStackBarChartScales>{renderChildren}</HorizontalStackBarChartScales>;
	},
	{ as: HorizontalStackBarChartScales },
);

export const PercentMarkLabelsOverride = element(
	() => {
		const { barStacks } = useElementProps(HorizontalStackBarChart.elements.MarkLabels);

		return (
			<g>
				{barStacks.map((barStack: BarStack) => {
					const total = barStack.bars.reduce((acc, bar) => {
						if (bar.datum.count) {
							return bar.datum.count + acc;
						}
						return acc;
					}, 0);

					return barStack.bars.map((bar) => {
						if (bar.width && bar.width > 30) {
							return (
								<text
									key={`bar-stack-label-${barStack.key}-${bar.key}`}
									x={bar.x + 12}
									y="50%"
									css={labelTextStyles}
									dominantBaseline="central"
								>
									{bar.width > 48 ? `${Math.round((bar.datum.count / total) * 100)}%` : '...'}
								</text>
							);
						}
						return null;
					});
				})}
			</g>
		);
	},
	{ as: HorizontalStackBarChart.elements.MarkLabels },
);

const labelTextStyles = css({
	font: token('font.body'),
	fontFamily: token('font.family.body'),
	fontWeight: token('font.weight.regular'),
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
	lineHeight: '1rem',
	fill: token('color.text.inverse', N0),
});

type Datum = {
	status: string;
	label: number;
};

export const CustomLegend = element(
	({ hide }) => {
		const { data, colorAccessor, colorScale } = useElementProps(
			HorizontalStackBarChart.elements.Legend,
		);

		if (hide) return null;

		// `data` is typed as unknown in the viz library
		// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
		const typedData = data as Datum[];

		return (
			<ul css={legendsContainerStyles}>
				{typedData.map((d) => {
					const symbolSize = 12;
					const color = colorScale(colorAccessor(d));

					return (
						<li key={d.label} css={singleLegendContainerStyles}>
							<div css={symbolStyles}>
								<svg width={symbolSize} height={symbolSize}>
									<rect fill={color} width={symbolSize} height={symbolSize} rx={1} ry={1} />
								</svg>
							</div>
							<span css={labelStyles}>
								{/* the string below comes from backend which is localised */}
								{d.label}
							</span>
						</li>
					);
				})}
			</ul>
		);
	},
	{ as: HorizontalStackBarChart.elements.Legend },
);

const legendsContainerStyles = css({
	display: 'flex',
	flexDirection: 'row',
	paddingLeft: 0,
	gap: token('space.200', '16px'),
});

const singleLegendContainerStyles = css({
	display: 'flex',
	marginTop: 0,
	gap: token('space.100', '8px'),
});

const symbolStyles = css({
	display: 'flex',
	justifyContent: 'center',
	alignItems: 'center',
});

const labelStyles = css({
	color: token('color.text.subtlest', '#626F86'),
	font: token('font.body.UNSAFE_small'),
	margin: '8px 0px',
	whiteSpace: 'nowrap',
});

const emptyIcon = (
	<svg width="96" height="96" viewBox="0 0 96 96" fill="none" xmlns="http://www.w3.org/2000/svg">
		<path
			d="M12.5428 15.7959C12.5428 15.7959 9.66627 33.5856 12.6719 42.7387H42.6239C42.6239 42.7387 40.1279 28.5517 42.8858 15.7959C42.8858 15.7959 12.3438 15.9914 12.5428 15.7959Z"
			fill="#DDDEE1"
		/>
		<path
			d="M47.1863 15.7959C47.1863 15.7959 44.3098 33.5856 47.3155 42.7387H77.2675C77.2675 42.7387 74.7715 28.5517 77.5293 15.7959C77.5293 15.7959 46.9874 15.9914 47.1863 15.7959Z"
			fill="#DDDEE1"
		/>
		<path
			d="M12.5428 45.4199C12.5428 45.4199 9.66627 63.2096 12.6719 72.3628H42.6239C42.6239 72.3628 40.1279 58.1757 42.8858 45.4199C42.8858 45.4199 12.3438 45.6154 12.5428 45.4199Z"
			fill="#DDDEE1"
		/>
		<path
			d="M54.9259 48.9873C54.9259 48.9873 48.6597 65.8833 49.8431 75.4484L79.2296 81.2433C79.2296 81.2433 79.5263 66.8433 84.6998 54.859C84.6998 54.859 54.6989 49.1444 54.9293 48.9873H54.9259Z"
			fill="#1868DB"
		/>
		<path
			d="M58.0503 63.1572L75.5328 66.8017"
			stroke="white"
			stroke-width="7"
			stroke-miterlimit="10"
		/>
		<path
			d="M68.6137 56.2383L64.9692 73.7173"
			stroke="white"
			stroke-width="7"
			stroke-miterlimit="10"
		/>
	</svg>
);

const emptyStatus = {
	count: 0,
	name: '',
	key: '',
	iconUrl: '',
};

const CustomHorizontalStackBarChart = ({
	epicStatus,
	legendOnly = false,
	onBarClick,
}: {
	epicStatus: EpicProgressProps['data']['progress'][0];
	legendOnly?: boolean;
	onBarClick?: () => void;
}) => {
	const { push } = useRouterActions();
	const { formatMessage } = useIntl();
	const { linksResolver, linksTarget } = useSummaryFeatures();
	const config: Record<string, { label: string; color: string }> = {
		done: {
			label: formatMessage(messages.doneLabel),
			color: token('color.chart.lime.bold', '#6A9A23'),
		},
		indeterminate: {
			label: formatMessage(messages.inProgressLabel),
			color: token('color.chart.blue.bold', B200),
		},
		new: {
			label: formatMessage(messages.toDoLabel),
			color: token('color.chart.neutral', N90),
		},
	};

	const allowedStatusCategories = ['done', 'indeterminate', 'new'];

	const filteredEpicStatus = legendOnly
		? allowedStatusCategories.map((status) => ({
				status,
				label: config[status].label,
				...emptyStatus,
			}))
		: epicStatus
				.sort(
					(a, b) =>
						allowedStatusCategories.indexOf(a.status) - allowedStatusCategories.indexOf(b.status),
				)
				// Filter to include only allowed status categories
				// This excludes 'unknown', which is a legacy status category for unmapped statuses
				.filter((status) => allowedStatusCategories.includes(status.status))
				.map((status) => ({ ...status, label: config[status.status].label }));

	const fillStyles = filteredEpicStatus.map(({ status }) => config[status].color);

	const onClickTooltip = useCallback(
		({ event }: { event?: PointerEvent | FocusEvent }) => {
			// this only gets emitted on a PointerEvent
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			const buttonNumber = (event as PointerEvent)?.button;
			if (buttonNumber === 2) {
				// ignore right clicks
				return;
			}

			onBarClick?.();
			const onClickHref = linksResolver({ filter: `parent = '${epicStatus[0].key}'` });

			if (buttonNumber === 1) {
				// middle button should open in new tab
				// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
				window.open(onClickHref, '_blank');
			} else if (linksTarget === '_self') {
				push(onClickHref);
			} else {
				// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
				window.open(onClickHref, linksTarget);
			}
		},
		[epicStatus, push, linksResolver, onBarClick, linksTarget],
	);

	return (
		<HorizontalStackBarChart
			data={filteredEpicStatus}
			xAccessor="name"
			yAccessor="count"
			colorAccessor="label"
		>
			<CustomLayout epicStatus={filteredEpicStatus} legendOnly={legendOnly} />
			<CustomLegend hide={!legendOnly} />
			<CustomScalesElement />
			<HorizontalStackBarChart.elements.AxisBottom hide />
			<HorizontalStackBarChart.elements.AxisLeft hide />
			<HorizontalStackBarChart.elements.Grid hide />
			<HorizontalStackBarChart.elements.TooltipOverlay hide />
			<HorizontalStackBarChart.elements.TooltipEmitter onClick={onClickTooltip} />
			<HorizontalStackBarChart.elements.Theme fillStyles={fillStyles} />
			<HorizontalStackBarChart.elements.Marks roundedCorners baseRoundedCorners />
			<PercentMarkLabelsOverride />
		</HorizontalStackBarChart>
	);
};

export const EpicProgress = ({
	data: { progress, hierarchyName, levelOneIssueTypeNames },
}: EpicProgressProps) => {
	const { formatMessage } = useIntl();
	const { linksResolver, linksTarget } = useSummaryFeatures();
	const { fireSectionAnalytics } = useSectionAnalytics(
		SectionIds.EpicProgress,
		BoundaryIds.EpicProgress,
	);

	const defaultHierarchyName = formatMessage(messages.defaultHierarchyName);
	const headingText = formatMessage(messages.title, {
		hierarchyName: hierarchyName ?? defaultHierarchyName,
	});

	const onBarClick = useCallback(() => {
		fireSectionAnalytics('viewWithFilter');
	}, [fireSectionAnalytics]);

	const handleLinkClick = useCallback(() => {
		fireSectionAnalytics('view');
	}, [fireSectionAnalytics]);

	if (!progress.length) {
		return (
			<EmptyState
				icon={emptyIcon}
				headingText={headingText}
				descriptionText={
					<FormattedMessage
						{...messages.emptyDescription}
						values={{
							link: (chunks) => (
								<Link
									href={`${getATLContextUrl('support')}/jira-software-cloud/docs/what-is-an-epic/`}
									target="_blank"
									onClick={handleLinkClick}
								>
									{chunks}
								</Link>
							),
						}}
					/>
				}
			/>
		);
	}

	return (
		<ChartWithHeaderContainer
			header={
				<>
					<Heading size="small" as="h2">
						{headingText}
					</Heading>
					<Text>
						<FormattedMessage
							{...messages.description}
							values={{
								link: (chunks) => (
									<Link
										href={linksResolver({
											filter: `type IN (${levelOneIssueTypeNames.map((name) => `'${escapeFilterChars(name)}'`).join(', ')})`,
										})}
										target={linksTarget}
										onClick={handleLinkClick}
									>
										{chunks}
									</Link>
								),
							}}
						/>
					</Text>
				</>
			}
			chart={
				<div css={containerStyles}>
					<CustomHorizontalStackBarChart epicStatus={progress[0]} legendOnly />
					{progress.map((epicStatus) => (
						<CustomHorizontalStackBarChart
							key={epicStatus[0].key}
							epicStatus={epicStatus}
							onBarClick={onBarClick}
						/>
					))}
				</div>
			}
		/>
	);
};

const containerStyles = css({
	overflowY: 'auto',
	marginRight: token('space.negative.150', '-12px'),
	paddingRight: token('space.150', '12px'),
});
